import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';

export interface ConversionState {
  code: string;
  symbol: string;
  rateFromGHS: number;
  country: string;
}

@Injectable({ providedIn: 'root' })
export class CurrencyService {
  // Free exchange API for IP-based fallback if needed
  private readonly EXCHANGE_API = 'https://api.exchangerate-api.com/v4/latest/GHS';
  private readonly GEO_API = 'https://ipapi.co/json/';

  // ── Defaults ──────────────────────────────────────────────────────────────
  private readonly DEFAULT_STATE: ConversionState = {
    code: 'GH₵',
    symbol: 'GH₵',
    rateFromGHS: 1,
    country: 'Ghana',
  };

  // ── Reactive state ────────────────────────────────────────────────────────
  private readonly _conversionState$ = new BehaviorSubject<ConversionState>(
    this.hydrateFromSession() ?? this.DEFAULT_STATE
  );

  /** All components subscribe to this for the active currency */
  readonly conversionState$: Observable<ConversionState> = this._conversionState$.asObservable();

  // Compat for code using the subject directly
  readonly userPreferenceState$: Observable<ConversionState> = this.conversionState$;
  readonly locationState$: Observable<ConversionState> = this.conversionState$;

  // ── Reactive Geolocation State ──────────────────────────────────────────────
  private cachedGeoState: ConversionState | null = null;
  private readonly _geolocationState$ = new BehaviorSubject<ConversionState>(this.DEFAULT_STATE);
  readonly geolocationState$: Observable<ConversionState> = this._geolocationState$.asObservable();

  constructor(private http: HttpClient) {
    // Sync with session storage on init
    this.refreshLocalCurrency();
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /**
   * Manually sets the app-wide currency preference.
   */
  setCurrency(code: string, symbol: string, country: string = ''): void {
    let finalCode = code.toUpperCase();
    let finalSymbol = symbol;

    if (finalCode === 'GHS' || finalCode === 'GHC') {
      finalCode = 'GH₵';
      finalSymbol = 'GH₵';
    }

    const newState: ConversionState = {
      code: finalCode,
      symbol: finalSymbol,
      rateFromGHS: 1,
      country: country || this._conversionState$.value.country
    };

    sessionStorage.setItem('currency', `${finalCode},${finalSymbol},${finalCode}`);
    if (country) sessionStorage.setItem('user_geo_country', country);

    this._conversionState$.next(newState);
    // Also update geolocation state to keep it in sync initially
    this._geolocationState$.next(newState);
  }

  /**
   * Syncs the service state with the current sessionId's currency preference.
   */
  refreshLocalCurrency(): Observable<ConversionState> {
    const state = this.hydrateFromSession() ?? this.DEFAULT_STATE;
    this._conversionState$.next(state);
    return of(state);
  }

  /**
   * Fetches the user's real IP and checks exchange rate to convert values
   * Only used in Subscription & Home components.
   */
  forceRefreshGeolocation(bypassCache: boolean = false): Observable<ConversionState> {
    if (this.cachedGeoState && !bypassCache) {
      console.log('[CurrencyService] Returning in-memory cached geolocation:', this.cachedGeoState.country, this.cachedGeoState.code);
      this._geolocationState$.next(this.cachedGeoState);
      return of(this.cachedGeoState);
    }

    console.log('[CurrencyService] Fetching geolocation from API...');
    return this.http.get<any>(this.GEO_API).pipe(
      map(res => {
        const country = res ? res.country_name || 'Ghana' : 'Ghana';
        const code = res ? res.currency || 'GHS' : 'GHS';
        const symbolMap: { [key: string]: string } = { 'USD': '$', 'GHS': 'GH₵', 'EUR': '€', 'GBP': '£', 'NGN': '₦' };
        const symbol = symbolMap[code] || code;

        const geoState: ConversionState = {
          code: code === 'GHS' || code === 'GHC' ? 'GH₵' : code,
          symbol: symbol,
          rateFromGHS: 1,
          country: country
        };

        console.log('[CurrencyService] Detected location:', country, code);
        this.cachedGeoState = geoState;
        this._geolocationState$.next(geoState);

        this.fetchExchangeRates(geoState.code).subscribe({
          next: (rate) => {
            geoState.rateFromGHS = rate;
            this.cachedGeoState = geoState;
            this._geolocationState$.next(geoState);
            console.log('[CurrencyService] Updated cached exchange rate for', geoState.code, 'to', rate);
          },
          error: (err) => {
            console.error('[CurrencyService] Failed to fetch exchange rates:', err);
            geoState.rateFromGHS = 1;
            this.cachedGeoState = geoState;
            this._geolocationState$.next(geoState);
          }
        });

        return geoState;
      }),
      catchError(err => {
        console.error('[CurrencyService] Geolocation fetch failed:', err);
        const fallback = this.DEFAULT_STATE;
        this.cachedGeoState = fallback;
        this._geolocationState$.next(fallback);
        return of(fallback);
      })
    );
  }

  private fetchExchangeRates(targetCode: string): Observable<number> {
    if (targetCode === 'GHS' || targetCode === 'GH₵' || targetCode === 'GHC') return of(1);

    return this.http.get<any>(this.EXCHANGE_API).pipe(
      map(data => {
        return (data && data.rates) ? data.rates[targetCode] || 1 : 1;
      }),
      catchError(err => {
        console.error('[CurrencyService] Exchange rates fetch failed:', err);
        return of(1);
      })
    );
  }

  /**
   * Placeholder for compatibility.
   */
  startDailyRefresh(): void { }

  /** 
   * Identity function for user (backend handles), mathematical for location.
   */
  convertFromGHS(amount: number, mode: 'user' | 'location' = 'user'): number {
    if (mode === 'location') {
      const rate = this._geolocationState$.value.rateFromGHS;
      return amount * rate;
    }
    return amount; // Backend already handles this if user mode
  }

  getCurrentSymbol(mode: 'user' | 'location' = 'user'): string {
    return mode === 'location' ? this._geolocationState$.value.symbol : this._conversionState$.value.symbol;
  }

  getCurrentCode(mode: 'user' | 'location' = 'user'): string {
    return mode === 'location' ? this._geolocationState$.value.code : this._conversionState$.value.code;
  }

  getCurrentState(mode: 'user' | 'location' = 'user'): ConversionState {
    return mode === 'location' ? this._geolocationState$.value : this._conversionState$.value;
  }

  // ─── Legacy compat ────────────────────────────────────────────────────────

  getCurrencyRegion(): Observable<any> {
    return this.conversionState$.pipe(
      map(state => ({
        currency: {
          currency_code: state.code,
          currency_name: state.code,
          currency_symbol: state.symbol,
        },
      }))
    );
  }

  getCurrencyRatings(to: string, amount: number): Observable<any> {
    const target = (to || 'USD').toUpperCase();
    return this.fetchExchangeRates(target).pipe(
      map(rate => {
        return {
          base: 'GH₵',
          rate: rate,
          result: { [target]: amount * rate }
        };
      })
    );
  }

  // ── Private Helpers ───────────────────────────────────────────────────────

  private hydrateFromSession(): ConversionState | null {
    const raw = sessionStorage.getItem('currency');
    const country = sessionStorage.getItem('user_geo_country') || '';

    if (raw) {
      const parts = raw.split(',');
      if (parts.length >= 3) {
        let code = parts[2]?.trim().toUpperCase();
        let symbol = parts[1]?.trim();

        // Ensure GHS/GHC code is replaced with GH₵ for display consistency
        if (code === 'GHS' || code === 'GHC') code = 'GH₵';
        if (code === 'GH₵' && !symbol) symbol = 'GH₵';

        return {
          code,
          symbol: symbol || 'GH₵',
          rateFromGHS: 1,
          country
        };
      }
    }
    return null;
  }
}
